import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { currentSession } from '@/api/client'
import { DOCUMENTATION } from '@/api/documentation'
import { ACCUEIL, peutUtiliserSuperuser } from '@/navigation'
import { useVuesStore } from '@/stores/vues'

/**
 * Routes de l'application.
 *
 * Chaque vue est chargée PARESSEUSEMENT (`() => import(...)`) : Vite en fait un
 * fragment séparé, et l'écran de connexion — le seul que voit un visiteur non
 * authentifié — ne télécharge pas le tableau de bord, ses graphiques et ses
 * dépendances.
 *
 * `meta.public` marque les routes accessibles sans session. Tout le reste exige
 * une session : la liste blanche est plus sûre que la liste noire, où un écran
 * ajouté sans `requiresAuth` devient accessible à tous sans qu'on le remarque.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  // Trois écrans PUBLICS d'accès au compte : acceptation d'invitation, demande
  // et réinitialisation de mot de passe. Leurs chemins sont stables par
  // construction — ils sont écrits dans les courriels envoyés (`core/mail/
  // templates.ts`) : renommer l'un d'eux sans toucher le gabarit, et tous les
  // liens déjà partis mènent au 404 de l'attrape-tout.
  {
    path: '/invitations/accept',
    name: 'invitation.accept',
    component: () => import('@/views/InvitationAcceptView.vue'),
    meta: { public: true },
  },
  {
    path: '/mot-de-passe-oublie',
    name: 'password.forgot',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/mot-de-passe/reinitialiser',
    name: 'password.reset',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  // Anciennes adresses de la documentation publique, désormais servie par le
  // backend (`/docs`) : un lien partagé ou mis en favori retombe sur la bonne
  // page. En production, le serveur web redirige avant même que le SPA charge ;
  // ceci couvre le développement, où SPA et API ont deux origines.
  {
    path: '/api',
    component: { render: () => null },
    meta: { public: true },
    beforeEnter: () => {
      window.location.replace(DOCUMENTATION.api)
      return false
    },
  },
  {
    path: '/mcp',
    component: { render: () => null },
    meta: { public: true },
    beforeEnter: () => {
      window.location.replace(DOCUMENTATION.mcp)
      return false
    },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      { path: '', name: ACCUEIL, component: () => import('@/views/AccueilView.vue') },
      {
        // Une vue définie en base (barre latérale « Vues ») : un seul écran
        // générique pour toutes. Le titre vient des données, pas des fichiers
        // de langue.
        //
        // La liste et ses fiches sont des ENFANTS d'un même parent : le lien
        // de la barre latérale (route vide `vue`) reste ainsi actif sur une
        // fiche ouverte depuis la vue.
        path: 'vues/:id',
        meta: {
          // Une sous-vue n'a pas d'entrée propre dans la barre : le nom vient
          // de la liste des vues, conteneur compris.
          titre: (to: RouteLocationNormalized) => useVuesStore().nomEcran({ genre: 'vue', id: String(to.params.id) }),
        },
        children: [
          { path: '', name: 'vue', component: () => import('@/views/VueView.vue') },
          // Fiche en pleine page : création depuis la vue, ou consultation.
          { path: 'nouvelle', name: 'vue.nouvelle', component: () => import('@/views/FicheView.vue') },
          {
            path: 'fiches/:recordId',
            name: 'vue.fiche',
            component: () => import('@/views/FicheView.vue'),
          },
        ],
      },
      {
        // Un tableau de bord : une PAGE comme une vue (racine de la barre ou
        // onglet d'un conteneur), qui mêle des chiffres de plusieurs tableaux.
        path: 'tableaux-de-bord/:id',
        name: 'tableau-de-bord',
        component: () => import('@/views/TableauDeBordView.vue'),
        meta: {
          titre: (to: RouteLocationNormalized) => useVuesStore().nomEcran({ genre: 'tableau_de_bord', id: String(to.params.id) }),
        },
      },
      {
        // Une fiche ouverte DIRECTEMENT par son tableau, sans vue : un tableau
        // existe sans vue (sous-tableau, cible de lien). Même écran que depuis
        // une vue ; seule la source change (`FicheView`).
        path: 'tableaux/:tableauId',
        children: [
          { path: 'nouvelle', name: 'tableau.nouvelle', component: () => import('@/views/FicheView.vue') },
          { path: 'fiches/:recordId', name: 'tableau.fiche', component: () => import('@/views/FicheView.vue') },
        ],
      },
      {
        // Le périmètre plateforme a son propre préfixe : l'URL dit dans quel
        // périmètre on se trouve, ce qui compte quand on colle un lien dans un
        // ticket.
        path: 'superuser/organisations',
        name: 'superuser.organisations',
        component: () => import('@/views/SuperuserOrganisationsView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/plans',
        name: 'superuser.plans',
        component: () => import('@/views/SuperuserPlansView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/services',
        name: 'superuser.services',
        component: () => import('@/views/SuperuserServicesView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/apparence',
        name: 'superuser.apparence',
        component: () => import('@/views/SuperuserApparenceView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'automatisations',
        name: 'automatisations',
        component: () => import('@/views/AutomatisationsView.vue'),
      },
      {
        // Historique de la structure et corbeille : réservés à qui peut modifier
        // la structure — le serveur refuse le reste en 403.
        path: 'structure',
        name: 'structure',
        component: () => import('@/views/StructureView.vue'),
        meta: { roles: ['owner', 'admin'] },
      },
      {
        // En dernier des enfants : la documentation est un recours, là où on
        // la cherche quand le reste n'a pas suffi.
        path: 'developpeurs',
        name: 'developpeurs',
        component: () => import('@/views/DevelopersView.vue'),
      },
    ],
  },
  {
    // Attrape-tout : une URL inconnue montre un écran explicite plutôt qu'une
    // page blanche, qui est indiscernable d'un plantage du bundle.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // Retour en haut de page à chaque navigation, sauf quand le navigateur
  // restitue une position (bouton Précédent) : on respecte alors son choix.
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

/**
 * Garde d'accès. Elle lit la SESSION PERSISTÉE et non le store Pinia : la garde
 * s'exécute avant le premier montage de composant, moment où le store peut ne
 * pas être hydraté. Le store reste la source de vérité pour l'affichage.
 */
router.beforeEach((to) => {
  const authenticated = currentSession() !== null
  const session = currentSession()

  if (!to.meta.public && !authenticated) {
    // `redirect` : après connexion, on renvoie l'utilisateur là où il allait.
    // Sans ça, un lien profond partagé par courriel retombe toujours sur
    // l'accueil, et l'utilisateur doit re-naviguer à la main.
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Déjà connecté et on demande l'écran de connexion : inutile de l'afficher.
  if (to.name === 'login' && authenticated) {
    return { name: ACCUEIL }
  }

  // --- Habilitations d'ÉCRAN -------------------------------------------------
  //
  // Le registre de navigation cache les entrées interdites ; cette garde refuse
  // l'ENTRÉE. Les deux sont nécessaires et ne font pas le même travail : un lien
  // profond, un signet ou une URL tapée à la main n'passent pas par le menu. Le
  // serveur reste l'autorité — ces contrôles évitent d'afficher un écran qui se
  // remplirait d'erreurs 403, ils ne protègent aucune donnée.
  const utilisateur = session?.user ?? null

  if (to.meta.scope === 'superuser' && !peutUtiliserSuperuser(utilisateur)) {
    return { name: ACCUEIL }
  }

  const rolesRequis = to.meta.roles as readonly string[] | undefined
  if (rolesRequis !== undefined && !rolesRequis.includes(utilisateur?.role ?? '')) {
    return { name: ACCUEIL }
  }

  return true
})
